import {
  SET_FILTERS,
  UNSET_FILTERS,
  SET_DEFAULT_SELECTION,
  UNSET_DEFAULT_SELECTION,
  CLEAR_FILTERS,
  CLEAR_DEFAULT_SELECTION,
  SET_FILTER_STATUS
} from '../mutationTypes'

const state = {
  filters: {},
  defaultSelection: {},
  transactionFilterStatus: false
}

const actions = {
  setFilter (store, payload) {
    this.commit(SET_FILTERS, payload)
  },
  unsetFilter (store, payload) {
    this.commit(UNSET_FILTERS, payload)
  },
  setDefaultSelection (store, payload) {
    this.commit(SET_DEFAULT_SELECTION, payload)
  },
  unsetDefaultSelection (store, payload) {
    this.commit(UNSET_DEFAULT_SELECTION, payload)
  },
  clearDefaultSelection (store) {
    this.commit(CLEAR_DEFAULT_SELECTION)
  },
  /**
   * Action that applies whatever filters have been set
   * @param store
   * @param payload
   * @return {Promise<{entities}>}
   */
  async applyFilters (store, payload) {
    const { filters } = this.state.filters
    if (filters.page) delete filters.page
    this.commit(SET_FILTER_STATUS, true)
    const { localSearch, searchAction, clearAction, clearMetadataAction, searchForDeps, lineFieldSearch } = payload
    if (localSearch) {
      return await store.dispatch(localSearch, filters)
    }
    const promises = [store.dispatch(clearAction, true)]
    if (clearMetadataAction) promises.push(store.dispatch(clearMetadataAction))
    try {
      await Promise.all(promises)
      const searchResp = await store.dispatch(searchAction, filters)
      this.commit(SET_FILTER_STATUS, false)
      if (lineFieldSearch && filters.search) {    // If lineFieldSearch is not undefined execute a search for the "keyword" criteria for the otherName field
        await store.dispatch('applyLineFieldSearch', { lineFieldSearch, filters, searchAction })
      }
      if (searchResp && searchResp.entities && searchForDeps) {
        // await store.dispatch('findDependencies', searchResp.entities)
        return searchResp
      }
    } catch (e) {
      this.commit(SET_FILTER_STATUS, false)
      console.log(e)
    }
  },
  /**
   * Searches for transactions where the keyword "search" filter value matches the value of a line field specified
   * in the "field" property of the object passed via the "lineFieldSearch" argument.
   * @param store
   * @param lineFieldSearch   { field, transactionType }
   * @param filters           The currently applied filters (e.g. { search: 'test', status__in: ['OPEN', 'AUDITED'] }
   * @param searchAction      The Vuex action to use to search for transactions
   * @return {Promise<void>}
   */
  async applyLineFieldSearch (store, { lineFieldSearch, filters, searchAction }) {
    let matchingTransactions = {}
    try {
      const { entities } = await store.dispatch('fetchLinesWithQuery', {
        type: lineFieldSearch.transactionType,
        query: { [`${lineFieldSearch.field}__icontains`]: filters.search }
      })
      if (entities && entities.lines && Object.values(entities.lines).length) {
        const transactionIds = [ ...new Set(Object.values(entities.lines).map(line => line.transaction_id)) ]   // obtain an array of unique "transaction_id"s from the lines returned
        const updatedFilters = Object.assign({}, filters, { id__in: transactionIds })   // clone the object containing the filter criteria and add the "id__in" property with the array of unique transaction ids
        delete updatedFilters.search  // remove the "search" property from the cloned filter criteria object because we don't want to search for transactions with the same criteria
        matchingTransactions = await store.dispatch(searchAction, updatedFilters)   // fetch the matching transactions
        await store.dispatch('findDependencies', matchingTransactions.entities)
      }
      return matchingTransactions
    } catch (e) {
      console.log(e)
      return matchingTransactions
    }
  },
  /**
   * Clears whatever filters have been set
   * @param store
   */
  clearFilters (store) {
    this.commit(CLEAR_FILTERS)
  }
}

const getters = {
  getFilters: (state) => {
    return state.filters
  },
  getDefaultSelection: (state) => {
    return state.defaultSelection
  },
  getTransactionFilterStatus: (state) => state.transactionFilterStatus
}

const mutations = {
  [SET_FILTER_STATUS] (state, payload) {
    state.transactionFilterStatus = payload
  },
  [SET_FILTERS] (state, payload) {
    if (!payload || !Object.keys(payload).length) return state.filters
    if (state.filters && Object.keys(state.filters).length) {
      state.filters = Object.assign({}, state.filters, payload)
    } else {
      state.filters = payload
    }
  },
  [UNSET_FILTERS] (state, payload) {
    if (payload && state.filters.hasOwnProperty(payload)) {
      let tmpState = Object.assign({}, state.filters)
      delete tmpState[payload]
      state.filters = Object.assign({}, tmpState)
    }
  },
  [SET_DEFAULT_SELECTION] (state, payload) {
    let tmpState = Object.assign({}, state.defaultSelection, payload)
    state.defaultSelection = Object.assign({}, tmpState)
  },
  [UNSET_DEFAULT_SELECTION] (state, payload) {
    let tmpState = Object.assign({}, state.defaultSelection)
    delete tmpState[payload]
    state.defaultSelection = Object.assign({}, tmpState)
  },
  [CLEAR_FILTERS] (state) {
    state.filters = Object.assign({})
    state.defaultSelection = Object.assign({})
  },
  [CLEAR_DEFAULT_SELECTION] (state) {
    state.defaultSelection = Object.assign({})
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
